const PIEVE_TOOL_RETRY_CLASS = Object.freeze({
  none: 'none',
  network: 'network',
  provider: 'provider',
  rateLimit: 'rate_limit',
  validation: 'validation'
});

const PIEVE_TOOL_PLANE = Object.freeze({
  desktopRelay: 'desktop_relay',
  knowledge: 'knowledge',
  sharedVault: 'shared_vault',
  mcp: 'mcp',
  web: 'web',
  subagent: 'subagent'
});

const PIEVE_WEB_SEARCH_FUNCTION_TOOL_NAME = 'web_search';
const PIEVE_WEB_SEARCH_TOOL_TYPES = new Set(['web_search_preview', 'web_search_preview_2025_03_11']);
const PIEVE_SUBAGENT_TOOL_NAME = 'create_subagent';
const PIEVE_AWAIT_SUBAGENTS_TOOL_NAME = 'await_subagents';

const DESKTOP_RELAY_TOOL_NAME_LIST = Object.freeze(['exec_command', 'shell_run', 'search_ripgrep', 'files_search', 'files_list', 'files_read', 'files_write', 'files_mkdir', 'files_stat', 'files_move', 'files_delete', 'files_preview', 'patch_apply', 'desktop_get_local_settings', 'desktop_update_local_settings']);

const PIEVE_DESKTOP_RELAY_TOOL_NAMES = new Set(DESKTOP_RELAY_TOOL_NAME_LIST);

const KNOWLEDGE_TOOL_NAME_LIST = Object.freeze(['knowledge_list', 'knowledge_read', 'knowledge_write', 'knowledge_stat', 'knowledge_move', 'knowledge_delete', 'knowledge_search_query', 'knowledge_share']);

const PIEVE_KNOWLEDGE_TOOL_NAMES = new Set(KNOWLEDGE_TOOL_NAME_LIST);
const PIEVE_SHARED_VAULT_TOOL_NAMES = new Set(KNOWLEDGE_TOOL_NAME_LIST);

const LOCAL_DISCOVERY_TOOL_NAME_LIST = Object.freeze(['search_ripgrep', 'files_search', 'files_list', 'files_read', 'files_preview', 'exec_command', 'shell_run']);
const PIEVE_LOCAL_DISCOVERY_TOOL_NAMES = new Set(LOCAL_DISCOVERY_TOOL_NAME_LIST);
const PIEVE_LOCAL_BROAD_DISCOVERY_TOOL_NAMES = new Set(LOCAL_DISCOVERY_TOOL_NAME_LIST);

const READ_ONLY_LOCAL_TOOL_NAME_LIST = Object.freeze(['search_ripgrep', 'files_search', 'files_list', 'files_read', 'files_preview', 'files_stat']);
const PIEVE_READ_ONLY_LOCAL_TOOL_NAMES = new Set(READ_ONLY_LOCAL_TOOL_NAME_LIST);
const PIEVE_DYNAMIC_SHELL_TOOL_NAMES = new Set(['exec_command', 'shell_run']);

const MCP_READ_TOOL_NAME_LIST = Object.freeze(['pieve_mcp_apps_search', 'pieve_mcp_apps_get', 'pieve_mcp_actions_search', 'pieve_mcp_lookup_resolve', 'pieve_mcp_action_schema', 'pieve_mcp_result_query']);

const SHARED_READ_TOOL_NAME_LIST = Object.freeze(['knowledge_list', 'knowledge_read', 'knowledge_stat', 'knowledge_search_query']);

const WEB_READ_TOOL_NAME_LIST = Object.freeze([PIEVE_WEB_SEARCH_FUNCTION_TOOL_NAME, 'read_web_page']);

// 2026-10-14: website_knowledge only reads approved files, public docs, or site search, so a
// website reply round runs it alongside catalog searches instead of after them.
const WEBSITE_READ_TOOL_NAME_LIST = Object.freeze(['website_knowledge']);

const PIEVE_API_PARALLEL_SAFE_TOOL_NAMES = new Set([...SHARED_READ_TOOL_NAME_LIST, ...MCP_READ_TOOL_NAME_LIST, ...WEB_READ_TOOL_NAME_LIST, ...READ_ONLY_LOCAL_TOOL_NAME_LIST, ...WEBSITE_READ_TOOL_NAME_LIST]);

const PIEVE_API_IDEMPOTENT_TOOL_NAMES = new Set([...SHARED_READ_TOOL_NAME_LIST, ...MCP_READ_TOOL_NAME_LIST, PIEVE_AWAIT_SUBAGENTS_TOOL_NAME, ...WEB_READ_TOOL_NAME_LIST, ...READ_ONLY_LOCAL_TOOL_NAME_LIST]);

const PIEVE_API_PROVIDER_RETRY_TOOL_NAMES = new Set([...MCP_READ_TOOL_NAME_LIST, ...WEB_READ_TOOL_NAME_LIST]);

const PIEVE_API_NETWORK_RETRY_TOOL_NAMES = new Set([...SHARED_READ_TOOL_NAME_LIST, ...READ_ONLY_LOCAL_TOOL_NAME_LIST]);

const DESKTOP_READ_TOOL_NAME_LIST = Object.freeze(['files_list', 'files_read', 'files_preview', 'files_stat', 'files_search', 'search_ripgrep']);
const PIEVE_DESKTOP_PARALLEL_SAFE_TOOL_NAMES = new Set(DESKTOP_READ_TOOL_NAME_LIST);
const PIEVE_DESKTOP_IDEMPOTENT_TOOL_NAMES = new Set([...DESKTOP_READ_TOOL_NAME_LIST, PIEVE_WEB_SEARCH_FUNCTION_TOOL_NAME]);
const PIEVE_DESKTOP_NETWORK_RETRY_TOOL_NAMES = new Set(DESKTOP_READ_TOOL_NAME_LIST);
const PIEVE_DESKTOP_PROVIDER_RETRY_TOOL_NAMES = new Set([PIEVE_WEB_SEARCH_FUNCTION_TOOL_NAME]);

const PIEVE_TOOL_PLANES = Object.freeze({
  ...Object.fromEntries([...PIEVE_DESKTOP_RELAY_TOOL_NAMES].map((toolName) => [toolName, PIEVE_TOOL_PLANE.desktopRelay])),
  ...Object.fromEntries(KNOWLEDGE_TOOL_NAME_LIST.map((toolName) => [toolName, PIEVE_TOOL_PLANE.knowledge])),
  ...Object.fromEntries(MCP_READ_TOOL_NAME_LIST.map((toolName) => [toolName, PIEVE_TOOL_PLANE.mcp])),
  ...Object.fromEntries(WEB_READ_TOOL_NAME_LIST.map((toolName) => [toolName, PIEVE_TOOL_PLANE.web])),
  [PIEVE_SUBAGENT_TOOL_NAME]: PIEVE_TOOL_PLANE.subagent,
  [PIEVE_AWAIT_SUBAGENTS_TOOL_NAME]: PIEVE_TOOL_PLANE.subagent
});

const toSafeToolName = (toolName) => {
  return String(toolName || '').trim();
};

const isPieveDesktopRelayToolName = (toolName) => {
  return PIEVE_DESKTOP_RELAY_TOOL_NAMES.has(toSafeToolName(toolName));
};

const resolvePieveToolPlane = (toolName) => {
  return PIEVE_TOOL_PLANES[toSafeToolName(toolName)] || null;
};

export { PIEVE_TOOL_RETRY_CLASS, PIEVE_TOOL_PLANE, PIEVE_TOOL_PLANES, PIEVE_WEB_SEARCH_FUNCTION_TOOL_NAME, PIEVE_WEB_SEARCH_TOOL_TYPES, PIEVE_SUBAGENT_TOOL_NAME, PIEVE_AWAIT_SUBAGENTS_TOOL_NAME, PIEVE_DESKTOP_RELAY_TOOL_NAMES, PIEVE_KNOWLEDGE_TOOL_NAMES, PIEVE_SHARED_VAULT_TOOL_NAMES, PIEVE_LOCAL_DISCOVERY_TOOL_NAMES, PIEVE_LOCAL_BROAD_DISCOVERY_TOOL_NAMES, PIEVE_READ_ONLY_LOCAL_TOOL_NAMES, PIEVE_DYNAMIC_SHELL_TOOL_NAMES, PIEVE_API_PARALLEL_SAFE_TOOL_NAMES, PIEVE_API_IDEMPOTENT_TOOL_NAMES, PIEVE_API_PROVIDER_RETRY_TOOL_NAMES, PIEVE_API_NETWORK_RETRY_TOOL_NAMES, PIEVE_DESKTOP_PARALLEL_SAFE_TOOL_NAMES, PIEVE_DESKTOP_IDEMPOTENT_TOOL_NAMES, PIEVE_DESKTOP_NETWORK_RETRY_TOOL_NAMES, PIEVE_DESKTOP_PROVIDER_RETRY_TOOL_NAMES, isPieveDesktopRelayToolName, resolvePieveToolPlane };
